import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errText } from "@/lib/api";
import { Shell } from "@/components/Shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, Upload, Pencil, Check } from "lucide-react";

export default function ManageStudents() {
  const [classes, setClasses] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [classId, setClassId] = useState("");
  const [division, setDivision] = useState("");
  const [students, setStudents] = useState([]);
  const [form, setForm] = useState({ roll_no: "", name: "" });
  const [newClass, setNewClass] = useState("");
  const [newDiv, setNewDiv] = useState("");
  const [edit, setEdit] = useState(null);
  const [upOpen, setUpOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);

  const loadClasses = async () => setClasses((await api.get("/classes")).data);
  const loadDivisions = async (cid = classId) => {
    if (!cid) return setDivisions([]);
    setDivisions((await api.get("/divisions", { params: { class_id: cid } })).data);
  };
  const loadStudents = async (cid = classId, div = division) => {
    if (!cid) return setStudents([]);
    const params = { class_id: cid };
    if (div) params.division = div;
    setStudents((await api.get("/students", { params })).data);
  };

  useEffect(() => { loadClasses(); }, []);
  useEffect(() => { loadDivisions(classId); loadStudents(classId, division); /* eslint-disable-next-line */ }, [classId, division]);

  const addClass = async () => {
    if (!newClass.trim()) return;
    await api.post("/classes", { name: newClass.trim(), order: classes.length + 1 });
    setNewClass(""); loadClasses(); toast.success("Class available");
  };

  const addDivision = async () => {
    if (!classId || !newDiv.trim()) return toast.error("Select a class and enter a division");
    try {
      await api.post("/divisions", { school_id: "", class_id: classId, name: newDiv.trim() });
      setNewDiv(""); loadDivisions(); toast.success("Division added");
    } catch (e) { toast.error(errText(e)); }
  };

  const addStudent = async (e) => {
    e.preventDefault();
    if (!classId || !division) return toast.error("Select class and division");
    try {
      await api.post("/students", { class_id: classId, division, roll_no: Number(form.roll_no), name: form.name });
      setForm({ roll_no: "", name: "" }); loadStudents(); toast.success("Student added");
    } catch (e) { toast.error(errText(e)); }
  };

  const saveEdit = async () => {
    try {
      await api.put(`/students/${edit.id}`, { class_id: edit.class_id, division: edit.division, roll_no: Number(edit.roll_no), name: edit.name });
      setEdit(null); loadStudents(); toast.success("Student updated");
    } catch (e) { toast.error(errText(e)); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (classId) fd.append("class_id", classId);
      if (division) fd.append("division", division);
      const { data } = await api.post("/students/bulk-preview", fd);
      setPreview(data);
      if (data.total === 0) toast.error("No rows detected in file");
    } catch (err) { toast.error(errText(err)); } finally { setUploading(false); e.target.value = ""; }
  };

  const confirmImport = async () => {
    const rows = preview.rows.filter((r) => r.valid).map((r) => ({
      class_id: r.class_id, division: r.division, roll_no: r.roll_no, name: r.name,
    }));
    if (!rows.length) return toast.error("No valid rows to import");
    try {
      const { data } = await api.post("/students/bulk-import", { rows });
      toast.success(`${data.created} students imported${data.skipped.length ? `, ${data.skipped.length} skipped` : ""}`);
      setPreview(null); setUpOpen(false); loadClasses(); loadDivisions(); loadStudents();
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <Shell title="Manage Students" subtitle="Classes → Divisions → Students (your school only)" nav={
      <Link to="/teacher"><Button variant="ghost" size="sm" data-testid="nav-back-teacher"><ArrowLeft className="h-4 w-4 mr-1" />Dashboard</Button></Link>
    }>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h4 className="text-base font-semibold">Step 1 — Classes</h4>
          <p className="text-xs text-slate-500 mt-1">Select the class you are setting up.</p>
          <div className="flex gap-2 mt-4">
            <Input placeholder="Add class e.g. Class 9" value={newClass} onChange={(e) => setNewClass(e.target.value)} data-testid="class-name-input" />
            <Button onClick={addClass} data-testid="add-class-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="classes-list">
            {classes.map((c) => (
              <button key={c.id} onClick={() => { setClassId(c.id); setDivision(""); }}
                data-testid={`class-select-${c.name.replace(/\s+/g, "-").toLowerCase()}`}
                className={`px-3 py-1.5 rounded-md text-sm border transition-colors ${classId === c.id ? "bg-primary text-white border-primary" : "bg-white border-slate-200 hover:bg-slate-50"}`}>
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h4 className="text-base font-semibold">Step 2 — Divisions</h4>
          <p className="text-xs text-slate-500 mt-1">Each class can have its own divisions.</p>
          <div className="flex gap-2 mt-4">
            <Input placeholder="A" value={newDiv} onChange={(e) => setNewDiv(e.target.value.toUpperCase())} data-testid="division-name-input" />
            <Button onClick={addDivision} data-testid="add-division-btn"><Plus className="h-4 w-4" /></Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="divisions-list">
            {!classId && <p className="text-sm text-slate-500">Select a class first</p>}
            {divisions.map((d) => (
              <span key={d.id} className={`px-3 py-1.5 rounded-md text-sm border flex items-center gap-2 ${division === d.name ? "bg-primary text-white border-primary" : "bg-white border-slate-200"}`}>
                <button onClick={() => setDivision(d.name)} data-testid={`division-select-${d.name}`}>Division {d.name}</button>
                <button onClick={async () => { await api.delete(`/divisions/${d.id}`); loadDivisions(); }} data-testid={`delete-division-${d.name}`}>
                  <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h4 className="text-base font-semibold">Step 3 — Add Student</h4>
          <form onSubmit={addStudent} className="space-y-3 mt-4">
            <div><Label>Roll Number</Label><Input type="number" required value={form.roll_no} onChange={(e) => setForm({ ...form, roll_no: e.target.value })} data-testid="student-roll-input" className="mt-1.5" /></div>
            <div><Label>Student Name</Label><Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="student-name-field" className="mt-1.5" /></div>
            <Button type="submit" className="w-full" data-testid="save-student-btn">Add Student</Button>
          </form>
          <Button variant="outline" className="w-full mt-3" onClick={() => { setUpOpen(true); setPreview(null); }} data-testid="bulk-upload-btn">
            <Upload className="h-4 w-4 mr-1" />Bulk Upload (CSV / Excel / PDF)
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between mt-10 mb-4 flex-wrap gap-3">
        <h3 className="text-xl font-head font-semibold">
          Students {classId ? `— ${classes.find((c) => c.id === classId)?.name}` : ""}{division ? ` Division ${division}` : ""} ({students.length})
        </h3>
      </div>
      <div className="bg-white border border-slate-200 rounded-lg overflow-x-auto">
        <Table data-testid="students-table">
          <TableHeader><TableRow><TableHead>Sr.</TableHead><TableHead>Roll No.</TableHead><TableHead>Name</TableHead><TableHead>Division</TableHead><TableHead>Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {students.map((s, i) => (
              <TableRow key={s.id} data-testid={`student-row-${s.roll_no}`}>
                <TableCell>{i + 1}</TableCell>
                <TableCell className="font-medium">{s.roll_no}</TableCell>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.division}</TableCell>
                <TableCell className="flex gap-3">
                  <button onClick={() => setEdit({ ...s })} data-testid={`edit-student-${s.roll_no}`}><Pencil className="h-4 w-4 text-slate-500" /></button>
                  <button onClick={async () => { if (window.confirm(`Delete ${s.name}?`)) { await api.delete(`/students/${s.id}`); loadStudents(); } }} data-testid={`delete-student-${s.roll_no}`}>
                    <Trash2 className="h-4 w-4 text-rose-600" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
            {students.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No students yet</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Edit Student</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Roll Number</Label><Input type="number" value={edit.roll_no} onChange={(e) => setEdit({ ...edit, roll_no: e.target.value })} data-testid="edit-roll-input" className="mt-1.5" /></div>
              <div><Label>Name</Label><Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="edit-name-input" className="mt-1.5" /></div>
              <div>
                <Label>Division</Label>
                <Select value={edit.division} onValueChange={(v) => setEdit({ ...edit, division: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="edit-division-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{divisions.map((d) => <SelectItem key={d.id} value={d.name}>Division {d.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <Button className="w-full" onClick={saveEdit} data-testid="save-edit-student-btn"><Check className="h-4 w-4 mr-1" />Save Changes</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={upOpen} onOpenChange={(v) => { setUpOpen(v); if (!v) setPreview(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>Bulk Upload Students</DialogTitle></DialogHeader>
          {!preview ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Upload a CSV, Excel (.xlsx) or PDF file with columns <b>Roll No, Student Name, Class, Division</b>.
                If Class/Division columns are missing, the currently selected class{division ? ` and Division ${division}` : ""} will be used.
              </p>
              <Input type="file" accept=".csv,.xlsx,.pdf" onChange={onFile} data-testid="bulk-file-input" />
              {uploading && <p className="text-sm text-slate-500">Reading file…</p>}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm" data-testid="preview-summary">
                {preview.valid} of {preview.total} rows are ready to import.
              </p>
              <div className="max-h-80 overflow-auto border border-slate-200 rounded-lg">
                <Table data-testid="preview-table">
                  <TableHeader><TableRow><TableHead>Roll</TableHead><TableHead>Name</TableHead><TableHead>Class</TableHead><TableHead>Div</TableHead><TableHead>Status</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {preview.rows.map((r, i) => (
                      <TableRow key={i} data-testid={`preview-row-${i}`}>
                        <TableCell>{r.roll_no ?? "—"}</TableCell>
                        <TableCell>{r.name || "—"}</TableCell>
                        <TableCell>{r.class_name || "—"}</TableCell>
                        <TableCell>{r.division || "—"}</TableCell>
                        <TableCell className={r.valid ? "text-emerald-700" : "text-rose-600"}>
                          {r.valid ? "Ready" : r.errors.join(", ")}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <div className="flex gap-2">
                <Button onClick={confirmImport} data-testid="confirm-import-btn">Import {preview.valid} Students</Button>
                <Button variant="outline" onClick={() => setPreview(null)} data-testid="cancel-preview-btn">Choose another file</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
